/* ---------------------------------------------------------------------------
   The kinds of document that get attached to the works.

   FIDIC Sub-Clause 4.21 asks a monthly progress report to carry (c) the
   manufacture and delivery status of main items and (e) copies of quality
   assurance documents, test results and certificates of materials. This is the
   short list of what those are, so that a report can group them and a reader
   can find the cube-crush certificate without opening nine files.

   DELIBERATELY SEVEN. The same argument the activity library makes at four
   times the length: a list long enough to need searching has stopped helping,
   and a document filed under the wrong one of seven headings is still findable
   while one filed under the wrong one of forty is not. Where a document is
   none of these, `other` carries it and its title does the work — and an
   `other` that keeps recurring is the argument for an eighth entry.

   NOT PER TENANT, for the reason nothing else in src/data/ is: a concrete cube
   result is a concrete cube result in Rawalpindi and in Syangja.

   WHAT IS NOT MODELLED HERE. No approval state, no revision, no supersession —
   see the header of migrations/0031_site_documents.sql. A document is a file
   somebody attached on a date.
   --------------------------------------------------------------------------- */

export type DocumentKind =
  | 'test-certificate'
  | 'material-certificate'
  | 'delivery-note'
  | 'method-statement'
  | 'survey-record'
  | 'ncr'
  | 'other';

export interface DocumentKindOption {
  id: DocumentKind;
  label: string;
  /** One sentence, in the words of somebody deciding where to file something. */
  note: string;
  /**
   * Which sub-clause of 4.21 this answers, where it answers one.
   *
   * CARRIED SO THE REPORT CAN GROUP BY IT rather than by a second list kept in
   * the report's own file. The mapping is a claim about the contract form and
   * belongs beside the vocabulary it is a claim about.
   */
  clause: 'c' | 'e' | null;
}

export const DOCUMENT_KINDS: readonly DocumentKindOption[] = [
  {
    id: 'test-certificate',
    label: 'Test certificate',
    note: 'Cube crush, pile integrity, weld test, compaction. The result of a test on the works.',
    clause: 'e',
  },
  {
    id: 'material-certificate',
    label: 'Material certificate',
    note: 'Mill certificate, cement or steel batch certificate — what a material was when it arrived.',
    clause: 'e',
  },
  {
    id: 'delivery-note',
    label: 'Delivery note',
    note: 'Main items arriving on site: girders, bearings, expansion joints.',
    clause: 'c',
  },
  {
    id: 'method-statement',
    label: 'Method statement',
    note: 'How an operation is to be carried out. Belongs to the structure rather than to one entry.',
    clause: 'e',
  },
  {
    id: 'survey-record',
    label: 'Survey record',
    note: 'Setting-out data, as-built levels, a check of line and level.',
    clause: 'e',
  },
  {
    id: 'ncr',
    label: 'Non-conformance and close-out',
    note: 'The written record of a non-conformance and what was done about it.',
    clause: 'e',
  },
  {
    id: 'other',
    label: 'Other',
    note: 'Anything the six above do not cover. The title is what makes it findable.',
    clause: null,
  },
];

const BY_ID = new Map(DOCUMENT_KINDS.map((k) => [k.id, k]));

export function isDocumentKind(value: unknown): value is DocumentKind {
  return typeof value === 'string' && BY_ID.has(value as DocumentKind);
}

/** Never empty, and never the raw id alone. */
export function documentKindLabel(id: string): string {
  return BY_ID.get(id as DocumentKind)?.label ?? `${id} (unknown)`;
}

/** Which sub-clause of FIDIC 4.21 a document of this kind answers, if any. */
export function documentClause(id: string): 'c' | 'e' | null {
  return BY_ID.get(id as DocumentKind)?.clause ?? null;
}

/**
 * What a document may be, as a file.
 *
 * PDF AND IMAGES, AND NOTHING ELSE. A test certificate arrives as a scan or a
 * PDF; a spreadsheet or a Word file is a working document rather than a
 * record, and accepting arbitrary types would make this bucket a place to put
 * anything and a place a browser is asked to render anything.
 *
 * Keyed by MIME type to the extension the key is built with, the same
 * arrangement `IMAGE_EXTENSIONS` has in functions/lib/photos.ts — so a type can
 * never be accepted and then have no extension to store it under.
 */
export const DOCUMENT_EXTENSIONS: Readonly<Record<string, string>> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

export function isDocumentType(contentType: string): boolean {
  return contentType in DOCUMENT_EXTENSIONS;
}

/**
 * Largest single document, in bytes.
 *
 * Bigger than a downscaled photograph and much smaller than a drawing set: a
 * scanned certificate is a page or two, and a fifty-megabyte upload from a
 * district connection is a request that will not finish. A backstop rather
 * than the working size.
 */
export const MAX_DOCUMENT_BYTES = 8 * 1024 * 1024;
